/* eslint-disable no-restricted-globals */

import { initializeApp } from 'firebase/app';
import { getAuth, initializeAuth, indexedDBLocalPersistence, multiFactor, setPersistence, browserLocalPersistence, connectAuthEmulator } from 'firebase/auth';
import { initializeFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { initializeAppCheck, ReCaptchaV3Provider, getToken } from 'firebase/app-check';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { Capacitor } from '@capacitor/core';
import { appCheckRenewal } from './utils/appCheckRenewal';


// Production App Check - debug token removed for production

const firebaseConfig = {
  apiKey: "AIzaSyCd1Do1xxHB-84pQcMTEkMPA5sI8ZMaswo",
  authDomain: "pedalxchange.firebaseapp.com",
  projectId: "pedalxchange",
  storageBucket: "pedalxchange.firebasestorage.app",
  messagingSenderId: "299253020024",
  appId: "1:299253020024:web:5fd8cac343542d9421c48d",
  measurementId: "G-8HW9NBLQEP"
};

const app = initializeApp(firebaseConfig);

// Initialize App Check only for production - skip for localhost to avoid phone auth conflicts
let appCheck;
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

if (!isLocalhost) {
  try {
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider('6Lfjf-grAAAAADxD_tJObYsFTddU1VqCudzJ1q8r'),
      isTokenAutoRefreshEnabled: false // Disabled to prevent rate limiting with multiple tabs
    });
    console.log('✅ App Check initialized successfully for production');
  } catch (error) {
    console.error('❌ App Check initialization failed:', error);
    console.warn('⚠️ Continuing without App Check - Firebase Auth rules will still protect resources');
    // Continue anyway - Firebase Auth rules (not App Check) will protect Storage
    // App Check should only enforce on Firestore/Functions where business logic lives
    // Note: Brave browser and strict privacy settings may block App Check's ReCAPTCHA
  }
} else {
  console.log('🔧 Skipping App Check for localhost to avoid phone auth conflicts');
}


const functions = getFunctions(app, 'us-central1');
// Optional: for local testing, uncomment this line:
// connectFunctionsEmulator(functions, 'localhost', 5001);


// Auth initialization differs by platform. In a native WebView the default
// getAuth() installs the popup/redirect resolver, which loads the gapi auth
// iframe from authDomain; that iframe cannot handshake with the capacitor://
// origin (throws "Error: t`capacitor"), leaving Auth's init unsettled and
// stalling Firestore's auth-token provider so reads hang forever. Calling
// initializeAuth WITHOUT a popupRedirectResolver skips the iframe entirely;
// IndexedDB persistence works in WKWebView, and email/password + phone MFA
// don't need the iframe. Web keeps the existing getAuth + persistence path.
let auth;
if (Capacitor.isNativePlatform()) {
  auth = initializeAuth(app, { persistence: indexedDBLocalPersistence });
} else {
  auth = getAuth(app);
  // Set persistence to local storage to survive redirects/page refreshes
  setPersistence(auth, browserLocalPersistence).catch((error) => {
    console.warn('⚠️ Failed to set auth persistence:', error);
  });
}

// Enable multi-factor authentication
auth.settings.appVerificationDisabledForTesting = false;


// Firestore transport. The default WebChannel streaming connection fails to
// establish inside native WebViews (iOS WKWebView / Android), which hangs
// reads indefinitely (getDocs/onSnapshot never resolve). Force long-polling on
// native where streaming is unreliable; auto-detect on web so the browser
// keeps streaming where it works and only falls back to long-polling if needed.
const db = initializeFirestore(
  app,
  Capacitor.isNativePlatform()
    ? { experimentalForceLongPolling: true }
    : { experimentalAutoDetectLongPolling: true }
);

// Local emulator hook (2026-10-30), for exercising rule-protected flows with
// synthetic data instead of production records. Compiled in only when
// REACT_APP_FIREBASE_EMULATORS=true is set at build/start time AND the page is
// served from localhost; a production build has the variable unset, so this
// whole block is dead code there. Firestore and Auth point at the default
// emulator ports; Functions/Storage are untouched (callables simply fail,
// which the pages already tolerate).
if (process.env.REACT_APP_FIREBASE_EMULATORS === 'true' &&
    typeof window !== 'undefined' && window.location.hostname === 'localhost') {
  connectFirestoreEmulator(db, 'localhost', 8080);
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
  console.warn('🧪 Firestore + Auth are pointed at the LOCAL EMULATORS (REACT_APP_FIREBASE_EMULATORS=true)');
}

const storage = getStorage(app);  // use default bucket

// Debug storage initialization
console.log('📦 Firebase Storage initialized:', {
  storageBucket: firebaseConfig.storageBucket,
  storage: storage,
  app: app
});

// appCheck is exported for utils/connectionRecoveryFirebase.js (manual "Try
// again" recovery renews its token through utils/appCheckRenewal.js).
// undefined on localhost and native, where App Check is never initialized.
export { app, auth, db, storage, functions, multiFactor, appCheck };

// ── App Check tab-return refresh (DIAGNOSTIC — remove if not the cause) ────
// Hypothesis: on iOS Safari, when a tab is backgrounded long enough for the
// App Check token to expire (~1h) OR for reCAPTCHA V3's iframe context to be
// torn down, the SDK's on-demand refresh on the next Firestore call hangs or
// fails silently. Result: queries return 0 docs and the page sits "loading"
// until a manual refresh. Affects both mobile browse and admin dashboard.
//
// This handler forces a fresh token when the user returns to a tab that was
// hidden ≥5min (a balance: long enough to skip quick app switches and
// bounded multi-tab reading, short enough to catch the bug window). Does
// NOT re-enable auto-refresh (which caused multi-tab rate limiting per
// commit d9ab87d). Fires at most once per hidden→visible cycle, and only
// on the actively-focused tab.
//
// Console logs are intentional and stay until we confirm App Check is the
// cause. Then we design the permanent fix and remove this entire block.
if (!isLocalhost && appCheck) {
  let lastHiddenAt = null;
  document.addEventListener('visibilitychange', async () => {
    if (document.hidden) {
      lastHiddenAt = Date.now();
      return;
    }
    if (!lastHiddenAt || Date.now() - lastHiddenAt < 5 * 60_000) return;
    const hiddenForSec = Math.round((Date.now() - lastHiddenAt) / 1000);
    lastHiddenAt = null;
    console.log(`[AppCheck test] Tab returned after ${hiddenForSec}s hidden — forcing App Check token refresh`);
    // Through the shared limiter (2026-10-06): one forced renewal per 2 min
    // shared with manual "Try again" recovery, per tab and, where the
    // browser supports it (Web Locks + storage), across open tabs, so the
    // triggers can't add up to the multi-tab rate limiting that d9ab87d
    // fixed. Never rejects; 'skipped' = renewed recently, 'pending' = an
    // earlier request in this tab is still running.
    const outcome = await appCheckRenewal.forceRenewal(appCheck, getToken);
    console.log(`[AppCheck test] Token refresh: ${outcome}`);
  });
}
